/**
 * One table shape for every credential list on Security.
 *
 * The four tabs used to invent their own columns — Label/Scope/Created/Rotated,
 * Key/Label/Type/Secret?, ID/Channel/Created/State, and a stacked form with no
 * table at all. Moving between them meant re-learning the table each time, for
 * four lists that answer the same three questions: what is it called, what does
 * it cover, and is it in a state you need to do something about.
 *
 * Each tab maps its own record onto those three. `scope` is what the credential
 * applies to; `status` is the one state worth acting on, not every flag the
 * record carries.
 */
import type { ReactNode } from 'react';

import { Badge, type BadgeTone } from '../ui/finixt.js';
import { DataTable } from './DataTable.js';

export interface CredentialRow {
  id: string;
  name: string;
  /** Human label or description, under the name; never its own column. */
  sub?: ReactNode;
  /** What it covers: a scope, a channel, a type. Rendered mono and muted. */
  scope: ReactNode;
  status: { label: string; tone: BadgeTone };
  actions?: ReactNode;
}

export interface CredentialTableProps {
  rows: CredentialRow[];
  count?: ReactNode;
  hint?: string | undefined;
  selectedKey?: string | undefined;
  onRowClick?: ((row: CredentialRow) => void) | undefined;
}

export function CredentialTable({
  rows,
  count,
  hint,
  selectedKey,
  onRowClick,
}: CredentialTableProps) {
  return (
    <DataTable
      rows={rows}
      count={count}
      hint={hint}
      selectedKey={selectedKey}
      onRowClick={onRowClick}
      columns={[
        {
          key: 'name',
          label: 'Name',
          mono: true,
          strong: true,
          wrap: true,
          render: (row) => (
            <>
              {row.name}
              {row.sub && (
                <span
                  style={{
                    display: 'block',
                    marginTop: 5,
                    fontSize: 'var(--text-meta)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {row.sub}
                </span>
              )}
            </>
          ),
        },
        { key: 'scope', label: 'Scope', mono: true, render: (row) => row.scope },
        {
          key: 'status',
          label: 'Status',
          render: (row) => <Badge tone={row.status.tone}>{row.status.label}</Badge>,
        },
        {
          key: 'actions',
          label: 'Actions',
          align: 'right',
          render: (row) =>
            row.actions ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                {row.actions}
              </span>
            ) : null,
        },
      ]}
    />
  );
}
